/**
 * @file Billing.tsx
 * @description
 * Billing page that displays payment records for the authenticated user.
 * Shows protocol title, invoice status, and provides links to pay or download receipts.
 *
 * Key Features:
 * - Display payments and related submission data in a table
 * - Allow users to pay invoices or download receipts
 * - Provide visual indicators for payment status
 *
 * @dependencies
 * - React
 * - useSupabase for DB queries
 * - useAuth for authentication state
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useAuth } from '../hooks/useAuth';
import withIdleTimer from '../hoc/withIdleTimer';
import UserNavBar from '../components/UserNavBar';

interface PaymentRecord {
  id: string;
  isOwnedByCurrentUser: boolean;
  submission_id: string;
  invoice_paid: boolean | null;
  invoice_receipt_link: string | null;
  invoice_id: string | null;
  submission_type: string;
  protocol_title: string | null;
}

const Billing: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, sessionExpired, signOut } = useAuth();
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingPaymentId, setOpeningPaymentId] = useState<string | null>(null);

  // Get supabase client
  const supabase = useSupabase();

  // Handle sign out - similar to SubmissionDetails implementation but navigating to '/'
  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  // Handle session expiration
  useEffect(() => {
    if (sessionExpired) {
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);

  // Fetch payment data when component mounts
  useEffect(() => {
    const fetchPaymentData = async () => {
      if (!user) return;

      setLoading(true);
      setError(null);

      try {
        const { data, error: queryError } = await supabase
          .from('payments')
          .select(`
            id,
            submission_id,
            invoice_paid,
            invoice_receipt_link,
            invoice_id,
            submission_type,
            submissions(data, user_id)
          `)
          .order('updated_at', { ascending: false });

        if (queryError) {
          console.error('Error fetching payment data:', queryError);
          setError('Failed to load payment data. Please try again later.');
          return;
        }

        // Transform the data to the format we need
        const transformedData: PaymentRecord[] = data
          ? data
              .filter(item => item.submissions) // Filter out any items without submission data
              .map(item => ({
                id: item.id,
                isOwnedByCurrentUser: item.submissions?.user_id === user.id,
                submission_id: item.submission_id,
                invoice_paid: item.invoice_paid,
                invoice_receipt_link: item.invoice_receipt_link,
                invoice_id: item.invoice_id,
                submission_type: item.submission_type,
                protocol_title: typeof item.submissions?.data === 'object' ? 
                  (item.submissions?.data as any)?.protocol_title || 'Untitled Protocol' 
                  : 'Untitled Protocol'
              }))
          : [];

        setPayments(transformedData);
      } catch (err) {
        console.error('Unexpected error fetching payment data:', err);
        setError('An unexpected error occurred. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, [user, supabase]);

  if (authLoading) {
    return (
      <div className="p-8 pt-24">
        <h1 className="text-xl font-bold mb-4">Loading...</h1>
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#F55D3E]"></div>
          <span className="ml-2 text-gray-500">Verifying your account</span>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="p-8 pt-24">
        <h1 className="text-xl font-bold mb-4">Billing & Payment</h1>
        <div className="bg-red-100 text-red-600 p-4 rounded mb-4">
          <p>You must be logged in to access this page.</p>
          <button
            onClick={() => navigate('/signin')}
            className="mt-2 px-3 py-2 bg-gray-300 text-gray-800 rounded"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const ownedPayments = payments.filter((payment) => payment.isOwnedByCurrentUser);
  const sharedPayments = payments.filter((payment) => !payment.isOwnedByCurrentUser);

  const openInvoiceLink = async (payment: PaymentRecord) => {
    setError(null);
    setOpeningPaymentId(payment.id);

    let paymentWindow: Window | null = null;
    try {
      paymentWindow = window.open('about:blank', '_blank');
      if (paymentWindow) {
        paymentWindow.opener = null;
        paymentWindow.document.write('<p>Loading payment link...</p>');
      }

      const { data, error: invokeError } = await supabase.functions.invoke('refresh_stripe_invoice_link', {
        body: { payment_id: payment.id },
      });

      if (invokeError) {
        throw invokeError;
      }

      const invoiceUrl = typeof data?.invoice_url === 'string' ? data.invoice_url : null;
      if (!invoiceUrl) {
        throw new Error('No invoice link returned.');
      }

      const invoicePaid = typeof data?.invoice_paid === 'boolean' ? data.invoice_paid : payment.invoice_paid;
      setPayments((current) =>
        current.map((item) =>
          item.id === payment.id
            ? { ...item, invoice_receipt_link: invoiceUrl, invoice_paid: invoicePaid }
            : item
        )
      );

      if (paymentWindow) {
        paymentWindow.location.href = invoiceUrl;
      } else {
        window.location.assign(invoiceUrl);
      }
    } catch (err) {
      console.error('Error opening invoice link:', err);
      if (paymentWindow) {
        paymentWindow.close();
      }
      setError('Unable to open a fresh Stripe invoice link. Please try again or contact support@timelyirb.com.');
    } finally {
      setOpeningPaymentId(null);
    }
  };

  const renderPaymentTable = (rows: PaymentRecord[]) => (
    <div className="mt-4 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Protocol Title
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Invoice Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Invoice/Receipt Link
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((payment) => (
            <tr key={payment.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {payment.protocol_title}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {payment.invoice_paid ? (
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Paid
                  </span>
                ) : (
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                    Payment Due
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {payment.invoice_receipt_link || payment.invoice_id ? (
                  <button
                    type="button"
                    onClick={() => openInvoiceLink(payment)}
                    disabled={openingPaymentId === payment.id}
                    className="text-blue-600 hover:text-blue-900 font-medium disabled:cursor-not-allowed disabled:text-gray-400"
                  >
                    {openingPaymentId === payment.id
                      ? 'Opening...'
                      : payment.invoice_paid
                        ? 'Download Receipt'
                        : 'Pay Now'}
                  </button>
                ) : (
                  <span className="text-gray-500">
                    {payment.invoice_paid ? 'No receipt available' : 'No payment link available'}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <UserNavBar user={user} onSignOut={handleSignOut} />
      <div className="p-8 pt-24">
        <h1 className="text-xl font-bold mb-4">Billing & Payment</h1>

        {error && (
          <div className="bg-red-100 text-red-700 border border-red-400 p-3 rounded mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center mt-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#F55D3E]"></div>
            <span className="ml-2 text-gray-500">Loading payment records...</span>
          </div>
        ) : payments.length === 0 ? (
          <div className="bg-gray-50 p-4 rounded border border-gray-200 text-center">
            <p className="text-gray-600">No payment records found.</p>
          </div>
        ) : (
          <div className="space-y-8">
            <section>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Owned Submissions</h2>
                <p className="text-sm text-gray-500">Billing records for submissions you created.</p>
              </div>
              {ownedPayments.length === 0 ? (
                <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-gray-600">
                  No billing records for submissions you own.
                </div>
              ) : (
                renderPaymentTable(ownedPayments)
              )}
            </section>

            <section>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Shared With Me</h2>
                <p className="text-sm text-gray-500">Shared submissions are read-only for edits, but payment links are available here.</p>
              </div>
              {sharedPayments.length === 0 ? (
                <div className="mt-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-gray-600">
                  No shared billing records found.
                </div>
              ) : (
                renderPaymentTable(sharedPayments)
              )}
            </section>
          </div>
        )}
      </div>
    </>
  );
};

export default withIdleTimer(Billing);
